/* @pareto-engineering/generator-front 1.1.1-alpha.2 */
import * as React from 'react';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import styleNames from '@pareto-engineering/bem/exports';
import "./styles.scss";
import { Preview } from "./common";
import { FormLabel, FormDescription } from "../../common";

// Local Definitions

const baseClassName = styleNames.base;
const componentClassName = 'file-upload';

/**
 * This is the component description.
 */
const FileUpload = ({
  id,
  className: userClassName,
  style,
  label,
  description,
  accept,
  multiple,
  name,
  disabled,
  maxCount,
  maxCountError,
  maxSize,
  maxSizeError,
  onChange,
  uploadStatus,
  filePreviewAlignment,
  optional,
  labelColor,
  color,
  viewOnly,
  handleFileDelete
  // ...otherProps
}) => {
  const [field,, helpers] = useField({
    name
  });
  const {
    value
  } = field;
  const {
    setError,
    setValue,
    setTouched
  } = helpers;
  const handleChange = event => {
    setTouched(true, false);
    const newFiles = Array.from(event.currentTarget.files);
    const currentFiles = value || [];
    const currentCount = currentFiles.length + newFiles.length;
    if (maxCount && currentCount > maxCount) {
      setError(maxCountError || `Maximum of ${maxCount} files allowed`);
      return;
    }
    const duplicateFiles = newFiles.filter(newFile => currentFiles.some(currentFile => currentFile.name === newFile.name));
    if (duplicateFiles.length > 0) {
      setError('You cannot upload the same file twice.');
      return;
    }
    if (maxSize) {
      const oversizedFiles = newFiles.filter(file => file.size > maxSize);
      if (oversizedFiles.length > 0) {
        setError(maxSizeError || `File size exceeds the limit of ${maxSize} bytes`);
        return;
      }
    }
    const updatedFiles = [...currentFiles, ...newFiles];
    setValue(updatedFiles);
    onChange?.(updatedFiles);

    // eslint-disable-next-line no-param-reassign
    event.target.value = null;
  };
  const handleDelete = ({
    fileIndex,
    identifier
  }) => {
    const updatedFiles = value?.filter((_, i) => i !== fileIndex);
    setValue(updatedFiles);
    handleFileDelete?.({
      fileIndex,
      identifier
    });
  };
  const acceptOptions = Array.isArray(accept) ? accept?.join(',') : accept;
  const isAnyFileUploading = Object.values(uploadStatus ?? {})?.map(e => e?.status)?.some(status => status === 'pending');
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: [baseClassName, componentClassName, userClassName, `preview-${filePreviewAlignment}`, `y-${color}`].filter(e => e).join(' '),
    style: style
  }, /*#__PURE__*/React.createElement("p", null, label), /*#__PURE__*/React.createElement(FormDescription, {
    className: "s-1",
    description: description,
    name: name
  }), !viewOnly && /*#__PURE__*/React.createElement(FormLabel, {
    name: name,
    color: labelColor,
    optional: optional
  }, /*#__PURE__*/React.createElement("input", {
    id: name,
    className: "file",
    type: "file",
    accept: acceptOptions,
    multiple: multiple || maxCount && maxCount > 0,
    disabled: disabled || isAnyFileUploading,
    onChange: handleChange
  }), /*#__PURE__*/React.createElement("span", {
    className: "ai-icon"
  }, "U"), /*#__PURE__*/React.createElement("span", null, "Attach file")), value?.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "attached-files"
  }, /*#__PURE__*/React.createElement("p", null, "Attached files:"), /*#__PURE__*/React.createElement("div", {
    className: "files"
  }, value.map((file, indx) => /*#__PURE__*/React.createElement(Preview, {
    file: file,
    key: file.name,
    writeOnly: !disabled,
    handleDelete: () => handleDelete({
      fileIndex: indx,
      identifier: file?.id || file.name
    }),
    uploadStatus: uploadStatus?.[file?.name]?.status,
    uploadProgress: uploadStatus?.[file?.name]?.progress
  })))));
};
FileUpload.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The label text for the file input
   */
  label: PropTypes.string,
  /**
   * The description text below the label
   */
  description: PropTypes.string,
  /**
   * The color of the text
   */
  color: PropTypes.string,
  /**
   * The accepted file types (e.g., 'image/*')
   */
  accept: PropTypes.string,
  /**
   *  Whether to allow multiple file uploads
   */
  multiple: PropTypes.bool,
  /**
   * The name attribute for the input
   */
  name: PropTypes.string.isRequired,
  /**
   * Whether the the input should be disabled
   */
  disabled: PropTypes.bool,
  /**
   * The maximum number of files allowed
   */
  maxCount: PropTypes.number,
  /**
   * The error message when max count is exceeded
   */
  maxCountError: PropTypes.string,
  /**
   * The maximum size of files allowed (in bytes)
   */
  maxSize: PropTypes.number,
  /**
   * The error message when file size exceeds max size
   */
  maxSizeError: PropTypes.string,
  /**
   * The upload progress value (0-100)
   */
  // progress:PropTypes.number,

  /**
   * The function to handle file change events
   */
  onChange: PropTypes.func,
  /**
   * The function to handle file delete events
   */
  handleFileDelete: PropTypes.func,
  /**
   * The upload status of the file with the file name as the key
   */
  uploadStatus: PropTypes.objectOf(PropTypes.string),
  /**
   * The alignment for file previews WRT the upload input
   */
  filePreviewAlignment: PropTypes.oneOf(['left', 'right', 'top', 'bottom']),
  /**
   * Whether the file upload is in view only mode
   */
  viewOnly: PropTypes.bool
};
FileUpload.defaultProps = {
  disabled: false,
  color: 'paragraph',
  filePreviewAlignment: 'bottom',
  viewOnly: false,
  multiple: true
};
FileUpload.Preview = Preview;
export default FileUpload;